import type { ButtonHTMLAttributes } from 'react';
import { memo } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import MantineTooltip from '../mantine-tooltip';
import type { RIconComponent } from '../remix-icon';
import RIcon from '../remix-icon';
import Typography from '../typography';

type Variant = 'action' | 'menu' | 'outline';
type Size = 'sm' | 'md' | 'lg';
const NUMERIC_SIZES: Record<Size, number> = {
  sm: 24,
  md: 32,
  lg: 40,
};

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: RIconComponent;
  variant?: Variant;
  label?: string;
  ariaLabel?: string;
  size?: Size;
  tooltip?: string;
}

const IconButton = ({
  icon,
  ariaLabel,
  variant = 'action',
  label,
  size = 'md',
  tooltip,
  type = 'button',
  ...rest
}: IconButtonProps) => {
  return (
    <MantineTooltip label={tooltip} disabled={rest.disabled}>
      <Wrapper>
        <IconWrapper
          type={type}
          aria-label={label ?? ariaLabel}
          variant={variant}
          size={NUMERIC_SIZES[size]}
          {...rest}
        >
          <RIcon icon={icon} size={size === 'lg' ? 'md' : 'sm'} />
        </IconWrapper>
        {label && (
          <StyledTypography
            variant={TypographyVariant.bodySm2}
            disabled={rest.disabled}
          >
            {label}
          </StyledTypography>
        )}
      </Wrapper>
    </MantineTooltip>
  );
};

export default memo(IconButton);

const Wrapper = styled.div`
  justify-items: center;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const StyledTypography = styled(Typography)`
  ${({ theme, disabled }: { theme: ITheme; disabled?: boolean }) => css`
    color: ${theme.palette.primary.main};
    margin-top: ${theme.spacing(0.5)};
    text-align: center;
    max-width: 85px;
    opacity: ${disabled ? 0.4 : 1};
  `}
`;

const baseStyle = ({ theme, size }: { theme: ITheme; size: number }) => css`
  width: ${size}px;
  height: ${size}px;
  border-radius: ${theme.borderRadius.full};
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;

  :disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  :focus-visible {
    outline: 2px solid ${theme.palette.focus};
  }
`;

const variantStyle = ({ theme }: { theme: ITheme }) =>
  variant({
    variants: {
      action: {
        backgroundColor: theme.palette.primary.bg,
        color: theme.palette.primary.main,
        ':hover:enabled': {
          bg: theme.palette.primary.main,
          color: theme.palette.neutral['00'],
        },
      },
      menu: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.neutral['00'],
        ':hover:enabled': {
          bg: theme.palette.primary.dark,
        },
      },
      outline: {
        bg: theme.palette.neutral['00'],
        border: `1px solid ${theme.palette.neutral[60]}`,
        ':hover:enabled': {
          bg: theme.palette.primary.bg,
        },
      },
    },
  });

const IconWrapper: StyledComponent<
  'button',
  DefaultTheme,
  { variant?: Variant; size: number }
> = styled('button')(baseStyle, variantStyle);
